import { AuthController } from "@somewear/auth";
import { useEffect, useRef, useState } from "react";

const SESSION_KEY = "external_auth_callback_port";
const SESSION_KEY_NONCE = "external_auth_nonce";
const SESSION_KEY_HOST = "external_auth_callback_host";

/**
 * Handles the external auth callback flow (e.g. from Beam CLI).
 *
 * When the app is opened with ?callback_port=<port>:
 * - If there's a stored session: signs out first, waits for fresh sign-in, redirects
 * - If no stored session: waits for sign-in, redirects directly
 *
 * Returns true while the redirect is in progress (caller should show a loader).
 */
export function useExternalAuth(
	authServiceLoaded: boolean,
	hasSession: boolean | undefined,
	activeAuthId: string | undefined,
): boolean {
	const [redirecting, setRedirecting] = useState(false);

	const [callbackPort] = useState(() =>
		new URLSearchParams(window.location.search).get("callback_port"),
	);
	const [callbackNonce] = useState(() =>
		new URLSearchParams(window.location.search).get("nonce"),
	);
	const [callbackHost] = useState(() =>
		new URLSearchParams(window.location.search).get("callback_host"),
	);

	const phase = useRef<"idle" | "signingOut" | "awaitingAuth">("idle");

	useEffect(() => {
		if (!callbackPort || !authServiceLoaded || hasSession === undefined) return;

		switch (phase.current) {
			case "idle":
				sessionStorage.setItem(SESSION_KEY, callbackPort);
				if (callbackNonce) sessionStorage.setItem(SESSION_KEY_NONCE, callbackNonce);
				if (callbackHost) sessionStorage.setItem(SESSION_KEY_HOST, callbackHost);
				if (hasSession) {
					if (activeAuthId === undefined) return;
					phase.current = "signingOut";
					AuthController.service.signOut$().subscribe();
				} else {
					phase.current = "awaitingAuth";
				}
				return;

			case "signingOut":
				if (activeAuthId !== undefined) return;
				phase.current = "awaitingAuth";
				return;

			case "awaitingAuth":
				if (activeAuthId === undefined) return;
				break;
		}

		const port = sessionStorage.getItem(SESSION_KEY);
		if (!port) return;
		const nonce = sessionStorage.getItem(SESSION_KEY_NONCE);
		const host = sessionStorage.getItem(SESSION_KEY_HOST) ?? "localhost";

		setRedirecting(true);
		AuthController.service.getTokenString$().subscribe((tokenString) => {
			if (tokenString) {
				const [type, token] = tokenString.split(" ", 2);
				sessionStorage.removeItem(SESSION_KEY);
				sessionStorage.removeItem(SESSION_KEY_NONCE);
				sessionStorage.removeItem(SESSION_KEY_HOST);
				const nonceParam = nonce ? `&nonce=${encodeURIComponent(nonce)}` : "";
				window.location.href = `http://${host}:${port}/api/auth/callback?access_token=${encodeURIComponent(token)}&token_type=${encodeURIComponent(type)}${nonceParam}`;
			} else {
				setRedirecting(false);
			}
		});
	}, [callbackPort, callbackHost, authServiceLoaded, hasSession, activeAuthId]);

	return redirecting;
}
